//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import cookies from 'js-cookie';

import { CompressedCookieStorage } from './CompressedCookieStorage';
import { EnumerableStorage } from './EnumerableStorage';
import { IEnumerableCognitoStorage } from './IEnumerableCognitoStorage';

/**
 * This module was added to solve DEV-44903.
 * Safari sandboxes localStorage access in iframes, so we can't reliably store
 * authentication tokens in localStorage if we want them to be available in iframes (e.g. bookmarklet).
 *
 * This module redirects amplify storage on Safari to use cookies for the Refresh Token and LastAuthUser.
 * With just these two pieces of information, amplify can generate new access tokens
 * and operate normally in the iframe.
 */

const idTokenRegex = /^CognitoIdentityServiceProvider.*idToken$/;

/**
 * Cognito storage keys that we will put in cookie storage.
 */
const cookieStoredKeyRegex = /^CognitoIdentityServiceProvider.*(LastAuthUser|refreshToken)$/;
/**
 * Should we use cookie storage for this key?
 */
function shouldUseCookieStorage(key: string): boolean {
    // We're on safari and this is one of the keys we want to put in cookieStorage.
  return cookieStoredKeyRegex.test(key);
}

// Create a fake id token that will pass the checks
// in this cognito js function:
// https://github.com/aws-amplify/amplify-js/blob/master/packages/amazon-cognito-identity-js/src/CognitoUser.js#L1169
// But also be expired.
function constructExpiredIdToken() {
    // JWTS are 3 base64Url-encoded segments separated by '.'.
    // Cognito doesn't look at any of the other fields if the jwt says it's expired.
    // So we can fake a jwt that just has `exp:0` in the payload,
    // and it will happily discard the rest and refresh the jwt.

    // This value was pre-compiled by running the following code:
    // return [{}, {exp: 0}, {}]
    //     .map((x) => JSON.stringify(x))
    //     .map((x) => Buffer.from(x, 'utf8').toString('base64'))
    //     .join('.');
  return 'e30=.eyJleHAiOjB9.e30=';
}

/**
 * Hybrid Amplify Auth Storage implementation shim.
 *
 * Stores most items in localStorage, but stores `LastAuthUser`
 * and `refreshToken` in cookieStorage.
 *
 * Also returns a fake expired `idToken` if a `refreshToken` is available and an idToken is not.
 * This convinces aws-amplify-auth to intiialize the session from the `refreshToken` alone.
 *
 * This approach is loosely based on discussions here:
 * https://github.com/aws-amplify/amplify-js/issues/1545#issuecomment-459325303
 */
export class HybridCognitoStorage implements IEnumerableCognitoStorage {

  public static createDefault() {
    return new HybridCognitoStorage(
            new EnumerableStorage(localStorage),
            new CompressedCookieStorage(cookies),
        );
  }

  constructor(
        private primaryStorage: IEnumerableCognitoStorage,
        private cookieStorage: IEnumerableCognitoStorage,
    ) {
  }

  public setItem(key: string, value: string): void {
    if (shouldUseCookieStorage(key)) {
      this.cookieStorage.setItem(key, value);
    } else {
      this.primaryStorage.setItem(key, value);
    }
  }
  public getItem(key: string): string | null {
    if (shouldUseCookieStorage(key)) {
      return this.cookieStorage.getItem(key);
    } else if (idTokenRegex.test(key) && this.shouldFakeIdToken(key)) {
            // Fake an expired idToken in storage
      return constructExpiredIdToken();
    } else {
      return this.primaryStorage.getItem(key);
    }
  }
  public removeItem(key: string): void {
    if (shouldUseCookieStorage(key)) {
      this.cookieStorage.removeItem(key);
    } else {
      this.primaryStorage.removeItem(key);
    }
  }
  public clear() {
        // clear any tokens stored in cookieStorage
    this.cookieStorage.clear();
    this.primaryStorage.clear();
  }

  public getAllKeys(): string[] {
    return [].concat(
            this.cookieStorage.getAllKeys(),
            this.primaryStorage.getAllKeys(),
        );
  }

    /**
     * Determine whether we need to supply Cognito with a fake IdToken in the current context.
     * @param requestedIdTokenKey the full idToken key of the current getItem request
     */
  private shouldFakeIdToken(requestedIdTokenKey: string): boolean {
        // Only relevant on safari, though compatible anywhere.
        // If we can't find an idToken in localStorage,
        // but we CAN find a refreshToken in cookieStorage,
        // then we are likely in a state where cookieStorage is available and localStorage is not.
        // We want to send a fake IdToken in this case to convince Amplify to try booting with only
        // the refreshToken.
    return !this.primaryStorage.getItem(requestedIdTokenKey) &&
            !!this.getItem(requestedIdTokenKey.replace(/idToken$/, 'refreshToken'));
  }
}
